/**
 * Datenzugriff der P5-Seiten der Sektion „Anfos & Umsatz": Bestellungen (/auswertungen/bestellungen) und Werkzeuge
 * (/auswertungen/werkzeuge). Backend: `node-app/src/routes/auswertungen.ts` Block „P5 Bestellungen + Werkzeuge",
 * Gruppe fit_products. Antworten im Hausformat `{ success, data }`.
 */
import { fetchData } from './apiUtils';
import type { DesignAbData, WebinarAffiliateGroupingOverview, WebinarOrderMappingData } from './webinarStatisticsData';

async function unwrap<T>(url: string): Promise<T> {
  const res = await fetchData<T>(url);
  if (!res.success || res.data === undefined) throw new Error(res.error || 'Laden fehlgeschlagen');
  return res.data;
}

const qs = (p: Record<string, string | undefined>): string => {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v) u.set(k, v);
  const s = u.toString();
  return s ? `?${s}` : '';
};

// ─── Bestellungen ─────────────────────────────────────────────────────────────────────────────

export interface BestellZeitraum {
  von: string;
  /** Letzter Bestelltag, inklusive. */
  bis: string;
  bis_exklusiv: string;
  tage: number;
  /** Ein Ende in der Zukunft wurde auf heute gesetzt. */
  bis_gekappt: boolean;
}

export interface BestellKennzahlen {
  /** Alle Bestellungen inkl. der von PL-X verweigerten (= Anfos + verweigert). */
  bestellungen: number;
  verweigert: number;
  /** verweigert ÷ Bestellungen, Prozent, ungerundet. */
  verweigert_anteil: number | null;
  anfos: number;
  /** Anteil an allen Anfos des Zeitraums, Prozent, ungerundet. */
  share: number | null;
  provisional: number;
  payers: number;
  /** Bisher bezahlt, netto. */
  paid_revenue: number;
  avg_age_days: number | null;
  with_value: number;
  /** Erwarteter Wert (bei Bestellung), Summe netto. */
  value_sum: number;
  value_per_anfo: number | null;
}

export interface BestellZeile extends BestellKennzahlen {
  key: string;
  label: string;
  detail: string | null;
  rest: boolean;
}

export interface BestellUebersicht {
  rows: BestellZeile[];
  total: BestellKennzahlen;
}

export type BestellDimension = 'produkt' | 'kanal' | 'blog' | 'wkz';

export interface BestellungenAntwort {
  zeitraum: BestellZeitraum;
  as_of: string;
  datenstand: string | null;
  anfo_wert_stand: string;
  uebersichten: Record<BestellDimension, BestellUebersicht>;
  /** Vergleich der Kacheln mit gleich langen Zeiträumen davor (Persona-Umbau A5). */
  vergleich: BestellVergleich | null;
}

export interface BestellVergleichWert {
  schnitt: number;
  min: number;
  max: number;
}

export interface BestellVergleich {
  /** Zahl der gleich langen Zeiträume (≥ 1). */
  zeitraeume: number;
  tage: number;
  von: string;
  bis_exklusiv: string;
  /** Jeder Zeitraum nur bis zur selben Stelle wie der gewählte (Datenstand), in Stunden ab Beginn; `null` = volle Tage. */
  gekuerzt_auf_stunden: number | null;
  bestellungen: BestellVergleichWert;
  anfos: BestellVergleichWert;
  verweigert: BestellVergleichWert;
  value_sum: BestellVergleichWert;
  value_per_anfo: number | null;
  /** Ab so vielen Zeiträumen steht ▲/▼, wenn der Wert außerhalb der Spanne liegt. */
  marke_ab: number;
}

export const fetchBestellungen = (startDate: string, endDate: string) =>
  unwrap<BestellungenAntwort>(`/api/auswertungen/bestellungen${qs({ startDate, endDate })}`);

// ─── Werkzeuge ────────────────────────────────────────────────────────────────────────────────

/** Webinar-Zuordnungen (wie der alte Tab „Webinar User Mappings": nur Bestelladressen mit FT-Eintritt). */
export const fetchWerkzeugZuordnungen = (startDate: string, endDate: string, mappingMethod?: string) =>
  unwrap<WebinarOrderMappingData[]>(
    `/api/auswertungen/werkzeuge/zuordnungen${qs({
      startDate,
      endDate,
      mappingMethod: mappingMethod && mappingMethod !== 'all' ? mappingMethod : undefined,
    })}`,
  );

export const fetchWerkzeugAffiliate = () =>
  unwrap<WebinarAffiliateGroupingOverview>('/api/auswertungen/werkzeuge/affiliate-gruppierungen');

export const fetchWerkzeugDesignAb = (startDate: string, endDate: string) =>
  unwrap<DesignAbData>(`/api/auswertungen/werkzeuge/design-ab${qs({ startDate, endDate })}`);
